import { useEffect, useRef, useState } from "react"
import { Link, useLocation } from "@tanstack/react-router"
import {
  getConsent,
  onConsentChange,
  setConsent,
  OPEN_PRIVACY_CHOICES_EVENT,
  type ConsentState,
} from "@/lib/utils/consent"
import { getCountryCodeFromPath } from "@/lib/utils/region"

/**
 * Opt-in consent banner. Shows on first visit (no choice yet, GPC not set), and
 * can be re-opened from the footer "Your Privacy Choices" link. Nothing
 * non-essential loads until the visitor accepts; declining keeps it off.
 */
const ConsentBanner = () => {
  const location = useLocation()
  const countryCode = getCountryCodeFromPath(location.pathname) || "us"
  const [state, setState] = useState<ConsentState | null>(null)
  const [reopened, setReopened] = useState(false)

  useEffect(() => {
    setState(getConsent())
    const unsub = onConsentChange(setState)
    const onOpen = () => setReopened(true)
    window.addEventListener(OPEN_PRIVACY_CHOICES_EVENT, onOpen)
    return () => {
      unsub()
      window.removeEventListener(OPEN_PRIVACY_CHOICES_EVENT, onOpen)
    }
  }, [])

  const bannerRef = useRef<HTMLDivElement>(null)
  const visible = !!state && (reopened || !state.decided)

  // The banner is fixed over the bottom of the viewport. While it's up, reserve
  // its height as scroll padding so the browser scrolls a keyboard-focused
  // element clear of it instead of leaving it hidden behind (WCAG 2.4.11).
  useEffect(() => {
    const banner = bannerRef.current
    if (!visible || !banner) return
    const root = document.documentElement
    const reserve = () => {
      root.style.scrollPaddingBottom = `${banner.offsetHeight}px`
    }
    reserve()
    const observer = new ResizeObserver(reserve)
    observer.observe(banner)
    return () => {
      observer.disconnect()
      root.style.scrollPaddingBottom = ""
    }
  }, [visible])

  // Consent state is client-only — render nothing during SSR / before mount to
  // avoid a hydration mismatch.
  if (!state || !visible) return null

  const choose = (analytics: "granted" | "denied") => {
    setConsent(analytics)
    setReopened(false)
  }

  return (
    <div
      ref={bannerRef}
      role="dialog"
      aria-label="Privacy choices"
      className="fixed inset-x-0 bottom-0 z-50 border-t border-[#FBD1C8]/30 bg-[#5F5156] text-[#FFF9F8] shadow-[0_-8px_24px_rgba(43,37,40,0.18)]"
    >
      <div className="content-container flex flex-col gap-4 py-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="max-w-2xl font-body text-sm leading-relaxed">
          <p>
            Mind if we use a few cookies to see what&rsquo;s working and help
            make +Bake better? The site works the same either way.{" "}
            {reopened && state.analytics === "granted" && !state.gpc
              ? "Analytics is currently on."
              : "Analytics and session tools stay off until you say yes."}{" "}
            See our{" "}
            <Link
              to="/$countryCode/privacy-policy"
              params={{ countryCode }}
              className="underline underline-offset-2 hover:text-white"
            >
              Privacy Policy
            </Link>
            .
          </p>
          {state.gpc && (
            <p className="mt-2 text-[#FCEAE7]">
              Your browser&rsquo;s Global Privacy Control signal is on, so
              analytics stays off automatically.
            </p>
          )}
        </div>
        <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
          <button
            type="button"
            onClick={() => choose("denied")}
            className="order-2 inline-flex items-center justify-center rounded-full border border-[#FFF9F8]/40 px-5 py-2.5 text-xs font-medium uppercase tracking-widest text-[#FFF9F8] transition-colors hover:bg-[#FFF9F8]/10 sm:order-1"
          >
            No thanks
          </button>
          <button
            type="button"
            onClick={() => choose("granted")}
            disabled={state.gpc}
            className="order-1 inline-flex items-center justify-center rounded-full bg-[#F5928A] px-5 py-2.5 text-xs font-medium uppercase tracking-widest text-[#2B2528] transition-colors hover:bg-[#f7a8a1] disabled:cursor-not-allowed disabled:opacity-50 sm:order-2"
          >
            Sure, help us improve
          </button>
        </div>
      </div>
    </div>
  )
}

export default ConsentBanner
